/**
 * STEP GROWTH-B2 · FAN BOOST public landing page
 * URL: /g/:sellerId?campaign={campaign_id}
 *
 * Flow (spec §7):
 *   FAN BOOST URL
 *     → attribution.landing (anonymous)
 *     → Kakao login (existing flow · REUSE)
 *     → attribution.login
 *     → auto POST /api/follow (existing · REUSE)
 *     → attribution.follow (dedup on server)
 *
 * Absolutely NO new auth · NO changes to /api/follow · NO custom-audience
 * uploads.  All heavy lifting stays on the server (growth_fan_boost.py).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle2, UserPlus, Loader2, AlertCircle, Heart } from 'lucide-react';
import api from '@/lib/api';
import SafeImg from '@/components/SafeImg';

const genSessionId = () => {
  try {
    const k = 'fb_sid';
    let sid = sessionStorage.getItem(k);
    if (!sid) {
      sid = 'sess_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
      sessionStorage.setItem(k, sid);
    }
    return sid;
  } catch { return 'sess_' + Date.now(); }
};

export default function FanBoostLanding({ user }) {
  const { sellerId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const campaignId = params.get('campaign') || '';
  const sessionId = useMemo(() => genSessionId(), []);

  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [campaign, setCampaign]   = useState(null);
  const [seller, setSeller]       = useState(null);
  const [status, setStatus]       = useState('idle'); // idle | following | followed | error
  const [isNewFollow, setIsNewFollow] = useState(null);

  // ── Step 1 · fetch campaign metadata + record landing (anonymous)
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!campaignId) {
        setError('유효하지 않은 캠페인 링크입니다.');
        setLoading(false);
        return;
      }
      try {
        const r = await api.get(`/growth/fan-boost/${campaignId}/public`);
        if (!mounted) return;
        // Belt-and-braces: seller in URL must match campaign owner.
        if (sellerId && r.data.seller?.seller_id && r.data.seller.seller_id !== sellerId) {
          setError('다른 판매자의 캠페인 링크입니다.');
          setLoading(false);
          return;
        }
        setCampaign(r.data);
        setSeller(r.data.seller);
      } catch (e) {
        setError(e?.response?.data?.detail || '캠페인 정보를 불러오지 못했습니다.');
        setLoading(false);
        return;
      }
      try {
        await api.post('/growth/attribution/event', {
          campaign_id: campaignId,
          event_type:  'landing',
          session_id:  sessionId,
          source:      'fanboost_landing',
        });
      } catch { /* landing tracking is best-effort */ }
      setLoading(false);
    })();
    return () => { mounted = false; };
  }, [campaignId, sellerId, sessionId]);

  // ── Step 2 · after Kakao login, record login + auto-follow + record follow
  useEffect(() => {
    if (!user || !campaign || status !== 'idle') return;
    (async () => {
      setStatus('following');
      // login event
      try {
        await api.post('/growth/attribution/event', {
          campaign_id: campaignId,
          event_type:  'login',
          session_id:  sessionId,
        });
      } catch { /* best-effort */ }
      // existing follow endpoint · REUSE, no changes to /api/follow core
      try {
        await api.post(`/follow?seller_id=${encodeURIComponent(seller.seller_id)}`);
      } catch (e) {
        setStatus('error');
        return;
      }
      try {
        const r = await api.post('/growth/attribution/event', {
          campaign_id: campaignId,
          event_type:  'follow',
          session_id:  sessionId,
        });
        setIsNewFollow(!!r.data?.is_new_follow);
      } catch { /* best-effort */ }
      setStatus('followed');
    })();
  }, [user, campaign, campaignId, sessionId, seller, status]);

  const goLogin = () => {
    // REUSE existing Kakao login flow via /login with return_url
    const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
    navigate(`/login?return_url=${returnUrl}`);
  };

  if (loading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center" data-testid="fanboost-loading">
        <Loader2 size={22} className="animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-6" data-testid="fanboost-error">
        <div className="max-w-md text-center">
          <AlertCircle size={40} className="mx-auto mb-3 text-[#FF3B30]" />
          <p className="font-bold">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-10 bg-gradient-to-b from-white to-[#FFF3F1] dark:from-[#0A0A0A] dark:to-[#141414]">
      <div className="w-full max-w-md bg-white dark:bg-[#141414] border border-black/5 dark:border-white/10 rounded-2xl p-6 shadow-lg" data-testid="fanboost-card">
        <div className="text-center">
          <div className="mx-auto w-24 h-24 rounded-full overflow-hidden bg-black/5 dark:bg-white/10 mb-4">
            <SafeImg src={seller?.profile_photo} kind="seller" alt={seller?.name} className="w-full h-full object-cover" />
          </div>
          <p className="text-xs uppercase tracking-widest text-[#FF3B30] font-black">단골 늘리기 광고</p>
          <h1 className="mt-1 text-2xl font-black" data-testid="fanboost-seller-name">{seller?.name || '판매자'}</h1>
          {seller?.bio && <p className="mt-2 text-sm text-gray-500 line-clamp-3">{seller.bio}</p>}
          <p className="mt-3 text-xs text-gray-400">현재 단골 수 {(seller?.follower_count || 0).toLocaleString()}명</p>
        </div>

        <div className="mt-6">
          {status === 'followed' ? (
            <div className="rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 p-4 flex items-center gap-3" data-testid="fanboost-followed">
              <CheckCircle2 size={22} />
              <div>
                <p className="font-bold text-sm">단골 등록 완료!</p>
                <p className="text-xs opacity-80">
                  {isNewFollow === true ? '새로운 단골로 등록되었습니다.' :
                   isNewFollow === false ? '이미 이 판매자의 단골이었습니다.' :
                   '단골 등록이 처리되었습니다.'}
                </p>
              </div>
            </div>
          ) : status === 'error' ? (
            <div className="rounded-xl bg-red-500/10 text-red-600 p-4" data-testid="fanboost-follow-error">
              <p className="font-bold text-sm">단골 등록 중 오류가 발생했어요. 다시 시도해주세요.</p>
              <button onClick={() => setStatus('idle')} className="mt-2 text-xs font-bold underline">다시 시도</button>
            </div>
          ) : !user ? (
            <button
              onClick={goLogin}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-[#FEE500] text-[#191919] font-black hover:brightness-95 transition"
              data-testid="fanboost-kakao-login"
            >
              <Heart size={16} /> 카카오로 단골 등록하기
            </button>
          ) : (
            <button
              disabled
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-gray-200 dark:bg-white/10 text-gray-500 font-black"
              data-testid="fanboost-following"
            >
              <Loader2 size={16} className="animate-spin" /> 단골 등록 중…
            </button>
          )}
        </div>

        <p className="mt-4 text-[11px] text-gray-400 text-center">
          카카오 계정으로 로그인하면 자동으로 이 판매자의 단골이 됩니다.
        </p>
      </div>
    </div>
  );
}
